<template>
  <div>
    <v-row class="my-1">
      <v-col cols="6">
        <v-autocomplete
          label="Estado"
          :items="estados"
          item-text="nombre"
          item-value="codigo"
          :value="selectedEstado && selectedEstado.codigo"
          :rules="requiredRules"
          :disabled="disabled"
          :loading="loadingEstados"
          :error-messages="catalogError"
          outlined
          dense
          hide-details="auto"
          @change="onEstadoChange"
        ></v-autocomplete>
      </v-col>
      <v-col cols="6">
        <v-autocomplete
          label="Municipio"
          :items="municipios"
          item-text="nombre"
          item-value="codigo"
          :value="selectedMunicipio && selectedMunicipio.codigo"
          :rules="requiredRules"
          :disabled="disabled || !municipios.length"
          outlined
          dense
          hide-details="auto"
          @change="onMunicipioChange"
        ></v-autocomplete>
      </v-col>
    </v-row>

    <v-row class="my-1">
      <v-col cols="8">
        <v-combobox
          label="Colonia"
          :items="coloniaNames"
          :value="value.neighborhood"
          :rules="requiredRules"
          :disabled="disabled || !selectedMunicipio"
          :loading="loadingColonias"
          outlined
          dense
          hide-details="auto"
          @change="onColoniaChange"
        ></v-combobox>
      </v-col>
      <v-col cols="4">
        <v-text-field
          label="Código postal"
          :value="value.postalCode"
          :rules="postalCodeRules"
          :disabled="disabled"
          maxlength="5"
          outlined
          dense
          hide-details="auto"
          @input="update({ postalCode: $event })"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row class="my-1">
      <v-col cols="6">
        <v-text-field
          label="Calle"
          :value="value.street"
          :rules="requiredRules"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ street: $event })"
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-text-field
          label="Número exterior"
          :value="value.number"
          :rules="requiredRules"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ number: $event })"
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-text-field
          label="Número interior"
          :value="value.extra"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ extra: $event })"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import MexicoGeoService from '../services/mexico-geo.service';
import { emptyAddress, normalizeAddress, isValidMexicanPostalCode } from '../utils/address.utils';

const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

// Dirección de México con el catálogo SEPOMEX: Estado → Municipio → Colonia
// (sugerida o escrita a mano) → código postal sugerido y editable.
// v-model: la dirección completa de address.utils (region/commune como { code, name }).
export default {
  name: 'MexicoAddressFields',
  props: {
    value: { type: Object, default: () => emptyAddress() },
    disabled: { type: Boolean, default: false },
    // Exige los obligatorios. El padre lo activa cuando hay (o hubo) dirección:
    // el backend rechaza una dirección de México incompleta.
    required: { type: Boolean, default: false }
  },
  data() {
    return {
      estados: [],
      colonias: [],
      loadingEstados: false,
      loadingColonias: false,
      catalogError: ''
    };
  },
  computed: {
    // Por código; si la dirección guardada no lo trae, por nombre.
    selectedEstado() {
      const { code, name } = normalizeAddress(this.value).region;
      return (
        this.estados.find(e => code && e.codigo === code) ||
        this.estados.find(e => name && sameName(e.nombre, name)) ||
        null
      );
    },
    municipios() {
      return this.selectedEstado ? this.selectedEstado.municipios : [];
    },
    selectedMunicipio() {
      const { code, name } = normalizeAddress(this.value).commune;
      return (
        this.municipios.find(m => code && m.codigo === code) ||
        this.municipios.find(m => name && sameName(m.nombre, name)) ||
        null
      );
    },
    coloniaNames() {
      return [...new Set(this.colonias.map(c => c.nombre))];
    },
    requiredRules() {
      return this.required ? [v => !!(v && String(v).trim()) || 'Este campo es requerido'] : [];
    },
    postalCodeRules() {
      return [
        ...this.requiredRules,
        v => !v || isValidMexicanPostalCode(v) || 'Debe tener 5 dígitos'
      ];
    }
  },
  watch: {
    selectedMunicipio: {
      immediate: true,
      handler(municipio, previous) {
        if (municipio && municipio.codigo !== (previous && previous.codigo)) {
          this.loadColonias(municipio.codigo);
        } else if (!municipio) {
          this.colonias = [];
        }
      }
    }
  },
  mounted() {
    this.loadEstados();
  },
  methods: {
    async loadEstados() {
      this.loadingEstados = true;
      const res = await MexicoGeoService.getEstados();
      if (res.status === 'ok') {
        this.estados = res.data;
        this.catalogError = '';
      } else {
        this.catalogError = 'No se pudo cargar el catálogo de México';
      }
      this.loadingEstados = false;
    },
    async loadColonias(municipioCode) {
      this.loadingColonias = true;
      const res = await MexicoGeoService.getColonias(municipioCode);
      // La colonia también se puede escribir a mano: sin catálogo, el campo sigue usable.
      this.colonias = res.status === 'ok' ? res.data : [];
      this.loadingColonias = false;
    },
    update(patch) {
      this.$emit('input', { ...normalizeAddress(this.value), ...patch });
    },
    onEstadoChange(codigo) {
      const estado = this.estados.find(e => e.codigo === codigo);
      this.update({
        region: { code: estado ? estado.codigo : '', name: estado ? estado.nombre : '' },
        commune: { code: '', name: '' },
        neighborhood: '',
        postalCode: ''
      });
    },
    onMunicipioChange(codigo) {
      const municipio = this.municipios.find(m => m.codigo === codigo);
      this.update({
        commune: { code: municipio ? municipio.codigo : '', name: municipio ? municipio.nombre : '' },
        neighborhood: '',
        postalCode: ''
      });
    },
    onColoniaChange(nombre) {
      const neighborhood = nombre || '';
      const colonia = this.colonias.find(c => c.nombre === neighborhood);
      const codes = colonia ? colonia.codigosPostales : [];
      const postalCode =
        codes.length && !codes.includes(this.value.postalCode) ? codes[0] : this.value.postalCode;
      this.update({ neighborhood, postalCode });
    }
  }
};
</script>
